import React, { useEffect, useState, useRef, JSX} from "react";
import { GiftVoucher, RusAuthProps, RusContextProps, RUSError, SubscriberLevel, WPError } from "../lib/definitions";
import styles from './redeem-gift-voucher.module.scss';
import useAuth, { subscriptionDescription, updateUserDetails2 } from "../lib/useAuth";
import { useRouter } from "next/router";
import Link from "next/link";
import Head from "next/head";
import { MenuId } from "../components/Header";
import Layout from "../components/Layout";
import { Button, TextInput, LoadingOverlay, Card, Text, Center, Space } from "@mantine/core";
import { consoleError } from "../lib/logger";
import { useForm } from '@mantine/form';
import { GetServerSideProps } from "next";
import { getUserInformation } from "../lib/data";
import { okModal, sessionStorageGetItem, sessionStorageRemoveItem, sessionStorageSetItem } from "../lib/utils";
import { getAuth } from '@clerk/nextjs/server'
import { useUser } from '@clerk/nextjs'
import SignInForm from "../components/SignInForm";

enum CartStage {

    enterVoucher=1,
    clerkSignIn,
    setUpAccount,
    alreadySubscribed,
    activate,
    done
}

interface CartContextValue {
  processing: boolean;
  setProcessing: (v: boolean) => void;
  refreshUserInformation: ReturnType<typeof useAuth>['refreshUserInformation'];
  navigateToCartStage: (stage: CartStage) => void;
  setGiftVoucher: (v: GiftVoucher | null) => void;
  giftRef: React.RefObject<GiftVoucher | null>;
}

const CartContext = React.createContext<CartContextValue>(null!);
const useCartContext = () => React.useContext(CartContext);

export default function RedeemVoucher({data} : {data: {cartStage : CartStage} & RusAuthProps & RusContextProps}) {

    const [processing, setProcessing] = useState(false);
    const giftRef=useRef<GiftVoucher|null>(null);

    const router = useRouter();


  useEffect(()=>
  {
    setProcessing(false);
  }, [data.cartStage]);

  const {refreshUserInformation} = useAuth();

     const emailForm = useForm({
    initialValues: { email: '', },
     validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),

    },
  });

 
  function setGiftVoucher(voucher : GiftVoucher | null)
  {
     giftRef.current = voucher;

        if (voucher)
        {            
            sessionStorageSetItem("rus-voucher", JSON.stringify(voucher));
        }
        else
        {
            sessionStorageRemoveItem("rus-voucher");
        }

    }


  useEffect(() =>
  {
    let x = sessionStorageGetItem("rus-voucher");

    if (x)
    {
        giftRef.current = JSON.parse(x);
    }
    else
    {
        giftRef.current = null;
    }

  }, []);

    function navigateToCartStage(stage: CartStage)
    {
        let url=router.pathname + "?stage=" + stage;

    router.push(url);

    }


    let cartComponent = <div></div>

    switch (data.cartStage) {
        case CartStage.alreadySubscribed:
            cartComponent = <AlreadySubscribedDiv />;
            break;
        case CartStage.setUpAccount:
            cartComponent = <SetupAccount />;
            break;
        case CartStage.done:
            cartComponent = <DoneDiv />;
            break;
        case CartStage.activate:
            cartComponent = <ActivateDiv />;
            break;
        case CartStage.enterVoucher:
            cartComponent = <EnterVoucherDiv />;
            break;
        case CartStage.clerkSignIn:
            cartComponent = <ClerkDiv />;
            break;
    }

    return(
        <Layout menuId={MenuId.subscribe} hidePopUp>
        <Head>
            <title>Raw Umber Subscription - Checkout</title>
            <meta name="description" content="Online art tutorials featuring artists from around the world. Plus a large library of videos and image references."></meta>
            <meta name="robots" content="noindex"/>
        </Head>
        <div className={styles.heroContainer}>
            <div className={styles.heroContent}>
            <LoadingOverlay visible={processing} overlayProps={{ blur: 2 }} transitionProps={{ duration: 500 }} />
        <Center style={{minHeight:400}}>
      
                <CartContext.Provider value={{processing, setProcessing,  navigateToCartStage, setGiftVoucher, giftRef, refreshUserInformation}}>

        <Card radius="md" p="xl" withBorder shadow='md' mt='xl' style={{overflow:'visible'}}>
   
        <Card.Section>
        <div className={styles.banner}>
              
                <Text fw={500} size='lg'>Redeeming a gift voucher</Text>             
                
                </div>
        </Card.Section>
        {cartComponent}

        </Card>
    
        </CartContext.Provider>
        </Center>
        <Space h="lg"/>
        </div>
        </div>
        </Layout>
    );

}


export const getServerSideProps : GetServerSideProps = async (context)=>
{
    let user = await getUserInformation(context.req);

    const stage = context.query?.stage;
    let cartStage = Number(stage) as CartStage;
    let newCartStage = cartStage;
    const { isAuthenticated } = getAuth(context.req);

    if (!Object.values(CartStage).includes(cartStage))
    {
        cartStage = CartStage.enterVoucher;
        newCartStage = cartStage;
    }

    
    const redirect = context.query?.redirect;

    if (redirect=='signin')
    {
        newCartStage = CartStage.clerkSignIn;
    }   


        if (newCartStage == CartStage.clerkSignIn) {
                if (isAuthenticated) {

                    if (!user.userName)
                    {
                        newCartStage = CartStage.setUpAccount;
                    }
                    else if (user.subscriber_level == SubscriberLevel.none ||user.expired) {
                        newCartStage = CartStage.activate;
                    }
                    else {
                        newCartStage = CartStage.alreadySubscribed;
                    }
                }
            }

        if (newCartStage == CartStage.setUpAccount && isAuthenticated && user.userName)
        {
            newCartStage = CartStage.activate;
        }

        if (isAuthenticated && user.subscribed && newCartStage!=CartStage.done)
        {
            newCartStage = CartStage.alreadySubscribed;
        }


    if (newCartStage!=cartStage)
    {
        let url = `/redeem-gift-voucher?stage=${newCartStage}`;
        return {
            redirect:
            {
              destination: url,
              permanent:false
            }
    
        }
    }

    return {
      props: {
        data: 
        { 
            cartStage: newCartStage,
        }
      }
  }

}

    function AlreadySubscribedDiv(): JSX.Element {

        const {userInformation} = useAuth();

        if (!userInformation)
        {
            return <></>;
        }
        return <div key="already-logged-on">You are already have a Raw Umber {subscriptionDescription(userInformation)} subscription. Go <Link href="/my-account/">here</Link> to manage your subscription.</div>
    }


    
    function EnterVoucherDiv()
    {
                
        const {setProcessing, navigateToCartStage, setGiftVoucher} = useCartContext();

        const voucherForm = useForm({
            initialValues:
            {
                voucherCode:"",
            }

        });

        async function submit(values: typeof voucherForm.values)
        {
            setProcessing(true);
             
            try
            {
                let at =  await fetch('/n/api/getvoucherinformation?code=' + values.voucherCode, {
                    method: 'GET',      cache: "no-store" as RequestCache                   
                });        
                let k : GiftVoucher | RUSError = await at.json();

                if (!at.ok)
                {
                    if ((k as any).message)
                    {
                        throw new RUSError((k as any).message);
                    }
                    else
                    {
                        throw new Error('');
                    }
                }

                if ((k as GiftVoucher).redeemed)
                {
                    throw new RUSError("This gift voucher has already been redeemed.");
                }

                setGiftVoucher(k as GiftVoucher);
                navigateToCartStage(CartStage.clerkSignIn);
            }
            catch (e)
            {
                if (e instanceof RUSError)
                {
                    okModal(e.message);
                }
                else
                {
                    okModal("There was a problem accessing the voucher");
                }
                setProcessing(false);
            }

            
           // setProcessing(false);
        }

        return(
            <div>

                <form onSubmit={voucherForm.onSubmit(async (values)=> await submit(values))}>
                    
                    <TextInput label="Please enter the gift voucher" type="text" required placeholder="ABCDEFGHIJ"
     
     {...voucherForm.getInputProps('voucherCode')}/>
     
                <Button type='submit' mt='sm'>Next</Button>
                </form>

            </div>);


    }

    function DoneDiv(): JSX.Element {

        return <><Text>Congratulations! You are now subscribed, and you can start exploring your subscription from <Link href='/my-account'>here</Link>.</Text>
        </>

    }

    function ActivateDiv(): JSX.Element {

        const {refreshUserInformation, navigateToCartStage, setProcessing, giftRef} = useCartContext();

        async function redeemVoucher()
        {
            try
            {
                setProcessing(true);

                let at =  await fetch('/n/api/processgiftvoucher', {
                    method: 'POST',     
                    headers: {
                        'Content-Type': 'application/json',
                      },     
                      credentials:"include",
                    body: JSON.stringify(
                    {
                        code: giftRef?.current?.code
                    } )
                });        
                let k : GiftVoucher | RUSError = await at.json();

                if (!at.ok)
                {
                    if ((k as any).message)
                    {
                        throw new RUSError((k as any).message);
                    }
                    else
                    {
                        throw new Error('');
                    }

                }

                refreshUserInformation();
                navigateToCartStage(CartStage.done);

            }
            catch (e)
            {
                setProcessing(false);
                if (e instanceof RUSError)
                {
                    okModal(e.message);
                }
                else
                {
                    consoleError(e);
                    okModal("There was a problem. Please try again later.");
                }
            }
        }

        return (<><Text mb='lg'>Finally, click the button below to redeem your voucher code.</Text>
        <Button onClick={async() => await redeemVoucher()}>Redeem voucher</Button>
        </>);

    }

    
    
        function SetupAccount()
        {
    
                    const { refreshUserInformation, navigateToCartStage, setProcessing} = useCartContext();
      
    
            
    
        const accountForm = useForm({
        initialValues:
        {
            firstName:"",
            lastName:"",
            userName:"",
    
        },
        validate:
        {
            userName: (value)=>
                {
                    const v = value.trim();
                    if (v.length<4 || v.length>63)
                    {
                        return 'Please use at least 4 characters for the user name';
                    }
    
                    if (!v.match(/^[a-zA-Z0-9_\-.]{4,64}$/))
                    {
                        return 'Please use only letters, numbers, and .-_ for the user name';
                    }
    
                    return null;
    
                },
    
            //password2: (value, values) => (value==values.password ? null : "The passwords don't match"),
        }
    
      });
      
         
            async function submit(values: typeof accountForm.values)
            {
    
                setProcessing(true);
    
                /*if ((await userExists(values.userName)).exists)
                {
                    okModal("Unfortunately that user name is already taken. Please try another.");
                    setProcessing(false);
                    return;
                }*/
    
                try
                {
                    await updateUserDetails2(values.userName,  values.firstName, values.lastName);
                }
                catch (e)
                {
                    console.error(e);
                    if (e instanceof WPError)
                    {
                        okModal((e as WPError).message);
                    }
                    else
                    {
                        okModal('There was a problem creating the user. Please try again later');
                    }
                    setProcessing(false);
                    return;
                }
    
                try
                {
               /* await logIn(cart.emailAddress!,
                        values.password,
                        false
                );*/
                }
                catch (e)
                {
                    okModal("There was a problem logging on. Please try again later");
                    console.error(e);
                    setProcessing(false);
                    return;
                } 
                finally
                {
                    refreshUserInformation();
                }
        
                navigateToCartStage(CartStage.activate);
            }
    
            return(
                <div>
    
                    <Text fw={700} ta='center'>Please let us know a few more details</Text>
                    <Space h='md'/>
                <form onSubmit={accountForm.onSubmit(async (values)=> await submit(values))}>
                    
                    <TextInput label="First name" type="text" required {...accountForm.getInputProps('firstName')}/>
                    <TextInput label="Last name" type="text" required {...accountForm.getInputProps('lastName')}/>
                    <TextInput label="User name. You will use this to log on, and it will be publicly visible" type="text" required {...accountForm.getInputProps('userName')}/>
                    <Space h='md'/>
                <Button type='submit'>Next</Button>
                </form>
    
            </div>);
    
        }
    

    function ClerkDiv()
    {
   

          const { isLoaded } = useUser();

          const url ='/redeem-gift-voucher?redirect=signin';
        return <>
        <div style={{display:'flex', justifyContent:'center', flexDirection:'column', alignItems:'center'}}>
                
</div>
<Center>
    
    <div style={{minHeight:600, minWidth:420, maxWidth:600}}>
        {isLoaded &&
        <SignInForm redirectUrl={url} signUpIfMissing prompt="Please enter the email address you would like to sign in with"/>
        }
        </div>
       
        </Center>
        </>
    }